import { MainLayout } from '@components/layouts/main.layout';
import { ReadinessTab } from '@components/pages/technology/components/shared/readiness-tab.component';
import { TechnologyTabLayout } from '@components/pages/technology/components/shared/technology-tab-layout.component';
import { TechnologyHeader } from '@components/pages/technology/components/technology-header.component';
import { TechnologyMetadataTab } from '@components/pages/technology/components/technology-metadata-tab.component';
import { TechnologyOrganizationTab } from '@components/pages/technology/components/technology-organization-tab.component';
import { TechnologyOverviewTab } from '@components/pages/technology/components/technology-overview-tab.component';
import { ErrorMessage } from '@components/shared/error-message.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Tile } from '@components/shared/tile.component';
import {
  useGetMissionAssessmentDraft,
  useGetMissionAssessmentDraftVersionsByRequest,
} from '@queries/mission-assessment-drafts.query';
import { useGetTechnology } from '@queries/technology.query';
import { TechnologyHeaderTab, parseAsTechnologyHeaderTab } from '@utils/query-state.util';
import { AnimatePresence, motion } from 'motion/react';
import { useQueryState } from 'nuqs';
import { Suspense } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MaContextColumn } from './components/ma-context-column.component';
import { MissionAssessmentForm } from './components/mission-assessment-form.component';

export const MissionAssessmentEditPage: React.FC = () => {
  const { missionAssessmentDraftId, technologyId } = useParams<{
    missionAssessmentDraftId: string;
    technologyId: string;
  }>();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('requestId') || undefined;
  const [selectedTab, setSelectedTab] = useQueryState('tab', parseAsTechnologyHeaderTab.withDefault('ma'));
  const { data: technology, isPending: isTechnologyLoading } = useGetTechnology(technologyId);
  const {
    data: missionAssessmentDraft,
    error,
    isFetching,
    isLoading,
  } = useGetMissionAssessmentDraft(missionAssessmentDraftId);
  const resolvedRequestId = missionAssessmentDraft?.request?.id ?? requestId ?? undefined;
  const { data: draftVersions } = useGetMissionAssessmentDraftVersionsByRequest(resolvedRequestId);

  const onToggleTab = (tab: TechnologyHeaderTab) => {
    void setSelectedTab(tab);
  };

  const renderContent = () => {
    if (!technologyId) {
      return null;
    }

    switch (selectedTab) {
      case 'overview':
        return (
          <TechnologyOverviewTab
            onNavigateToTab={onToggleTab}
            technologyId={technologyId}
          />
        );
      case 'details':
        return <TechnologyMetadataTab technologyId={technologyId} />;
      case 'organization':
        if (isTechnologyLoading) {
          return (
            <Tile
              isLoading={true}
              loadingState={{ message: 'Loading organization...' }}
            />
          );
        }
        return <TechnologyOrganizationTab technology={technology} />;
      case 'trl':
        return <ReadinessTab type="trl" />;
      case 'mrl':
        return <ReadinessTab type="mrl" />;
      case 'ma':
        if (isLoading) {
          return (
            <Tile
              isLoading={true}
              loadingState={{ message: 'Loading draft...' }}
            />
          );
        }
        if (error) {
          return (
            <Tile header={{ title: 'Error' }}>
              <ErrorMessage message={error.errors?.[0]?.message || 'Failed to load mission assessment draft.'} />
            </Tile>
          );
        }
        if (!missionAssessmentDraft) {
          return null;
        }
        return (
          <TechnologyTabLayout
            contextColumn={
              <MaContextColumn
                requestId={resolvedRequestId}
                technologyId={technologyId}
              />
            }
            isTransitioning={isFetching && !isLoading}
            mainContent={
              <Suspense fallback={null}>
                <MissionAssessmentForm
                  draftVersions={draftVersions}
                  isTransitioning={isFetching && !isLoading}
                  mode="update"
                  missionAssessmentDraft={missionAssessmentDraft}
                  requestId={requestId}
                  technologyId={technologyId}
                />
              </Suspense>
            }
            variant="content-context"
          />
        );
      default:
        return null;
    }
  };

  return (
    <PageviewTracker>
      <HelmetWrapper title="Mission Assessment" />
      <MainLayout
        fullWidth
        className="max-w-full"
      >
        <div className="w-full flex flex-col gap-4">
          <TechnologyHeader
            onToggleTab={onToggleTab}
            selectedTab={selectedTab}
          />

          <div className="@container/technology mx-auto w-full max-w-[1600px] px-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </MainLayout>
    </PageviewTracker>
  );
};
